import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Autocomplete, Box, Button, Divider, MenuItem, Stack, TextField, Typography } from '@mui/material'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import { apiGet } from '../../../../generic/Api_Functions'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'
import { systemValueLabel } from '../../../../generic/systemValues/systemValueLabel'
import {
  DOCUMENT_SOURCE_TITLE_KEYS,
  SHARED_QUALITY_CATALOGUE_TYPES,
  contributionTargetsOf
} from './fabricationDocumentSources'
import { normalizeForSearch } from './fabricationDocumentSearch'

// Historia 148 (S68/S69/S71) — AÑADIR DESDE EL PUESTO.
//
// ⚠️ RN21: un documento no se añade «a la fabricación». Se añade a un REGISTRO concreto, y el
// operario lo elige en dos pasos: primero el TIPO de procedencia, después el registro. Los tipos
// ofrecidos son solo los que llegan con `add` (D9: el permiso es lo único que acota el destino).
//
// ✅ HUECO CERRADO POR LA ENMIENDA E2 (RN41/S97). Los destinos ya NO se derivan de los documentos
// recibidos —que solo conocían los registros CON documento— sino de `targets`, que Sun calcula
// ANTES de podar por lo que tiene documento. «RM-022», sin ningún documento, vuelve a ser un
// destino legítimo. Y no cuesta ni una petición más: `targets` viaja en la misma respuesta.
//
// ⚠️ RN41.8/S97: `targets` llega SIN deduplicar. La misma feature alcanzada desde dos tareas viaja
// dos veces; elegir una u otra produce el mismo alta (D19), así que el selector la ofrece UNA vez
// con sus rutas reunidas. La deduplicación es presentación: el contrato no deduplica.
//
// ⚠️ F3/S118: el catálogo de tipos sale de `GET /endpoints/documentTypes`, el endpoint que MIC
// YA tiene y que usan también `Document.jsx` y `DocumentsCarouselView.jsx`. Una sola lectura por
// apertura del formulario, no una por cada cambio de destino, y **ningún cambio en Sun**.
//
// ⚠️ V6/V7/S71: las validaciones son las que MIC YA usa, con SUS literales. Esta historia no
// modifica ninguna validación documental vigente ni acuña literales nuevos salvo el del nombre
// vacío, que MIC resuelve hoy con el `mandatory` de su tabla y no tiene texto propio.
//
// ⚠️ LA FORMA DE LA PANTALLA — `features/S120-refinamientos-de-diseno-jupiter.feature`, S120-S126.
// El alta se estiraba por los
// 92vw × 85vh del modal —sustituyéndolo entero— para pintar dos filas de botones y dejar debajo
// media pantalla vacía. Ahora es un **modal SUPERPUESTO y acotado**: la documentación sigue viéndose
// detrás, que es la referencia de dónde va lo que se está subiendo. Los dos pasos y los metadatos
// caben a la vez:
//
//   · El TIPO sigue siendo una rejilla de botones grandes: son nueve como mucho, se ven todos de un
//     vistazo y se pulsan con guante (RN30/D18, 48 px).
//   · El REGISTRO pasa a DESPLEGABLE CON BÚSQUEDA: puede haber **muchos** —una fabricación real
//     trae decenas de materias primas— y además llevan RUTA —«Ø20 H7 · T-01, T-02 › FG-3
//     Dimensional»—, que en un botón se corta y en una opción cabe entera. Bajar treinta líneas con
//     el dedo para encontrar un tornillo no es buscar: se escribe y se filtra. El filtro es el MISMO
//     que el de la lista de documentos —subcadena, sin mayúsculas y sin acentos (D10)— y mira el
//     código, el nombre Y la ruta, porque el operario recuerda cualquiera de los tres.
//   · Y cuando el tipo trae UN SOLO destino, el registro se elige **solo**. Un job tiene UNA orden de
//     fabricación, UNA referencia de pieza, UN proceso y UNA operación: ahí el desplegable tenía una
//     única opción y obligaba a un toque que no decide nada. Se sigue viendo cuál es —el destino
//     tiene que estar a la vista antes de subir (RN21)—, pero ya viene puesto.
//   · El fichero tiene ZONA DE SOLTAR, que es lo que llena el hueco vertical que antes sobraba, y
//     se puede pulsar: en el puesto se usa el selector, pero desde un portátil se arrastra.
//
// El flujo NO cambia: primero el tipo, después el registro, y nada de «añadir a la fabricación»
// sin elegir uno (S68). Lo que cambia es dónde y cómo se pulsa.
const MAX_FILE_NAME_LENGTH = 80

// CUATRO columnas: con los ocho tipos que una fabricación suele traer salen dos filas llenas, sin la
// fila huérfana de dos botones que dejaba el ajuste automático. En pantalla estrecha baja a dos.
const TYPE_GRID_SX = {
  display: 'grid',
  gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
  gap: 1
}
const TYPE_BUTTON_SX = { minHeight: 48, minWidth: 48, textTransform: 'none', justifyContent: 'flex-start' }
const dropZoneSx = (isHovered, hasFile) => ({
  border: '2px dashed',
  borderColor: isHovered ? 'primary.main' : (hasFile ? 'success.main' : 'divider'),
  borderRadius: 1,
  bgcolor: isHovered ? 'action.hover' : 'transparent',
  p: 3,
  minHeight: 120,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 1,
  cursor: 'pointer',
  transition: 'border-color 0.2s, background-color 0.2s'
})

/**
 * @param {Object} props
 * @param {Array} props.groups Los grupos recibidos, para derivar destinos y registros
 * @param {Function} props.onSubmit Recibe `({ source, payload, file })`
 * @param {Function} props.onCancel
 */
const FabricationDocumentAddForm = ({ groups = [], onSubmit = () => {}, onCancel = () => {} }) => {
  const { t_ } = useSmartTranslation()
  const [sourceType, setSourceType] = useState('')
  const [record, setRecord] = useState('')
  const [documentName, setDocumentName] = useState('')
  const [description, setDescription] = useState('')
  const [file, setFile] = useState('')
  const [documentTypes, setDocumentTypes] = useState([])
  const [documentTypeId, setDocumentTypeId] = useState('')
  const [validationMessage, setValidationMessage] = useState('')
  const [isDropHovered, setIsDropHovered] = useState(false)
  const fileInput = React.useRef(null)

  // Solo los grupos con `add` Y con algún destino: sin registro no hay nada que ofrecer (RN41.3).
  const addableGroups = groups.filter((group) => group.permissions?.add && (group.targets?.length || 0) > 0)

  useEffect(() => {
    let isCurrent = true
    apiGet('documentTypes')
      .then((types) => {
        if (!isCurrent) {
          return
        }
        const list = Array.isArray(types) ? types : []
        setDocumentTypes(list)
        const general = list.find((type) => type?.name?.toLowerCase() === 'general') || list[0] || ''
        setDocumentTypeId(general === '' ? '' : general.id)
      })
      .catch(() => {
        if (isCurrent) {
          setDocumentTypes([])
        }
      })
    return () => {
      isCurrent = false
    }
  }, [])

  const targetsOf = (candidateSourceType) =>
    contributionTargetsOf(addableGroups.find((group) => group.sourceType === candidateSourceType))

  const recordsOfSelectedType = () => targetsOf(sourceType)

  // D19/S98 — en features y grupos de feature el documento NO cuelga de la tarea desde la que se
  // pulsó: cuelga del catálogo de calidad, que comparten otras fabricaciones, piezas y procesos.
  // El operario tiene que saberlo ANTES de subir nada.
  const writesIntoSharedCatalogue = SHARED_QUALITY_CATALOGUE_TYPES.includes(sourceType)

  const pickType = (nextSourceType) => {
    setSourceType(nextSourceType)
    // Cambiar de tipo invalida el registro elegido: pertenecía al tipo anterior. Y si el tipo nuevo
    // solo tiene un destino, se elige ya: obligar a abrir un desplegable de una opción es pedir un
    // toque que no decide nada (y en el puesto cada toque cuesta, con guantes).
    const targets = targetsOf(nextSourceType)
    setRecord(targets.length === 1 ? targets[0] : '')
  }

  // Código Y nombre: «RM-014» no dice qué pieza es, y el operario elige por lo que conoce. Los dos
  // vienen en `targets`, no hace falta nada de Sun. Cuando el contrato no trae nombre propio
  // —features— `name` repite el código, y entonces se escribe una sola vez.
  const recordLabel = (target) => (target.name && target.name !== target.code
    ? target.code + ' \u00b7 ' + target.name
    : target.code)

  // El mismo criterio que la búsqueda de documentos (D10): subcadena, sin mayúsculas y sin acentos
  // —en planta se escribe sin tildes—. Mira código, nombre y ruta.
  const filterTargets = (options, { inputValue }) => {
    const needle = normalizeForSearch(inputValue).trim()
    if (needle === '') {
      return options
    }
    return options.filter((target) =>
      normalizeForSearch(target.code + ' ' + target.name + ' ' + target.path).includes(needle))
  }

  const takeFile = (nextFile) => {
    setIsDropHovered(false)
    if (nextFile) {
      setFile(nextFile)
    }
  }

  const handleSubmit = () => {
    if (documentName.trim() === '') {
      setValidationMessage('Document name is required')
      return
    }
    if (file === '') {
      // El literal de MIC, sin acuñar uno nuevo (S71).
      setValidationMessage('Select a file')
      return
    }
    if (file.name && file.name.length > MAX_FILE_NAME_LENGTH) {
      setValidationMessage('File name cannot exceed 80 characters')
      return
    }
    // F3/S119 — NUNCA un tipo de documento vacío hacia Sun. El catálogo puede faltar porque la
    // lectura de `documentTypes` falló o porque respondió una lista vacía; en los dos casos el alta
    // se queda aquí y lo dice. Antes salía con el tipo vacío, la capa de API lo serializaba como
    // indefinido y el endpoint de alta —que exige el tipo— respondía **400 sin literal propio**:
    // un 400 mudo en una pantalla de planta es una pantalla que no dice nada.
    //
    // ⚠️ Va DESPUÉS de las validaciones de V6/V7: el aviso del catálogo no se adelanta al de MIC
    // sobre el nombre o el fichero, que son lo que el operario puede arreglar él mismo (S71).
    if (documentTypeId === '' || documentTypes.length === 0) {
      setValidationMessage('The document type catalogue is not available. Close the form and open it again to retry.')
      return
    }
    setValidationMessage('')
    onSubmit({
      // La identidad viaja TAL CUAL venía en `targets` —texto en los nueve tipos, UUID en los dos
      // nuevos— sin convertirla (S88/S97).
      source: { sourceType, sourceReferenceId: record.referenceId, sourceCode: record.code },
      payload: {
        documentName: documentName.trim(),
        description,
        documentType: documentTypes.find((type) => type.id === documentTypeId) || '',
        order: 1
      },
      file
    })
  }

  return (
    <Box data-testid='add-document-card' sx={{ width: '100%', textAlign: 'left' }}>
      <Stack spacing={3}>
        <Typography variant='subtitle1' sx={{ fontWeight: 800 }}>
          {t_('Choose where to add the document')}
        </Typography>

        {/* Paso 1: el TIPO de procedencia. */}
        <Stack spacing={1}>
          <Typography variant='overline' color='text.secondary'>{t_('Destination')}</Typography>
          <Box data-testid='add-destination-types' sx={TYPE_GRID_SX}>
            {addableGroups.map((group) => (
              <Button
                key={group.sourceType}
                variant={sourceType === group.sourceType ? 'contained' : 'outlined'}
                onClick={() => pickType(group.sourceType)}
                sx={TYPE_BUTTON_SX}
              >
                {t_(DOCUMENT_SOURCE_TITLE_KEYS[group.sourceType] || group.sourceType)}
              </Button>
            ))}
          </Box>
        </Stack>

        {/* Paso 2: el REGISTRO concreto. No hay «añadir a la fabricación» sin elegir uno. */}
        {sourceType !== ''
          ? (
            <Box data-testid='add-destination-records'>
              <Autocomplete
                fullWidth
                openOnFocus
                autoHighlight
                options={recordsOfSelectedType()}
                value={record === '' ? null : record}
                onChange={(event, target) => setRecord(target || '')}
                getOptionLabel={recordLabel}
                isOptionEqualToValue={(option, value) => option.referenceId === value.referenceId}
                filterOptions={filterTargets}
                noOptionsText={t_('No results')}
                renderOption={(optionProps, target) => {
                  const { key: ignoredKey, ...rest } = optionProps
                  return (
                    <Box component='li' {...rest} key={target.referenceId} data-reference-id={target.referenceId} sx={{ minHeight: 48 }}>
                      <Stack alignItems='flex-start'>
                        <span>{recordLabel(target)}</span>
                        {/* La ruta distingue destinos que comparten código, y reúne las de la misma
                            identidad: «T-01, T-02 › FG-3 Dimensional». */}
                        {target.path !== ''
                          ? <Typography variant='caption' color='text.secondary'>{target.path}</Typography>
                          : null}
                      </Stack>
                    </Box>
                  )
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t_('Record')}
                    inputProps={{ ...params.inputProps, 'aria-label': t_('Record') }}
                  />
                )}
              />
            </Box>
            )
          : null}

        {writesIntoSharedCatalogue
          ? (
            <Typography color='warning.main' variant='body2'>
              {t_('This document belongs to the shared quality catalogue: it hangs from the feature or feature group itself, not from the task it was pressed from, and will be visible from every task, fabrication, part or process that uses it.')}
            </Typography>
            )
          : null}

        {record !== ''
          ? (
            <Stack spacing={2} data-testid='add-document-form'>
              <Divider />
              <TextField
                label={t_('Document name')}
                value={documentName}
                onChange={(event) => setDocumentName(event.target.value)}
                inputProps={{ 'aria-label': t_('Document name') }}
              />
              <TextField
                label={t_('Description')}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                inputProps={{ 'aria-label': t_('Description') }}
              />
              {documentTypes.length > 0
                ? (
                  <TextField
                    select
                    label={t_('Type')}
                    value={documentTypeId}
                    onChange={(event) => setDocumentTypeId(event.target.value)}
                    inputProps={{ 'aria-label': t_('Type') }}
                  >
                    {documentTypes.map((type) => (
                      <MenuItem key={type.id} value={type.id}>{systemValueLabel(t_, 'document_type', type)}</MenuItem>
                    ))}
                  </TextField>
                  )
                : null}

              {/* La zona de soltar es lo que llena el hueco que antes quedaba vacío. El `input` sigue
                  existiendo —oculto— para que el selector del sistema sea el mismo de siempre. */}
              <Box
                data-testid='add-document-drop-zone'
                onClick={() => fileInput.current?.click()}
                onDragOver={(event) => { event.preventDefault(); setIsDropHovered(true) }}
                onDragLeave={() => setIsDropHovered(false)}
                onDrop={(event) => { event.preventDefault(); takeFile(event.dataTransfer?.files?.[0]) }}
                sx={dropZoneSx(isDropHovered, file !== '')}
              >
                <UploadFileIcon color={file === '' ? 'disabled' : 'success'} />
                <Typography variant='body2' color='text.secondary' align='center'>
                  {file === '' ? t_('Drag the file here or press to choose it') : file.name}
                </Typography>
              </Box>
              <input
                type='file'
                ref={fileInput}
                data-testid='add-document-file'
                style={{ display: 'none' }}
                onChange={(event) => takeFile(event.target.files?.[0])}
              />

              {validationMessage !== ''
                ? <Typography color='error'>{t_(validationMessage)}</Typography>
                : null}
            </Stack>
            )
          : null}

        <Stack direction='row' spacing={1} justifyContent='flex-end'>
          <Button variant='outlined' onClick={onCancel} sx={{ minHeight: 48, minWidth: 48 }}>
            {t_('Cancel')}
          </Button>
          {record !== ''
            ? (
              <Button variant='contained' onClick={handleSubmit} sx={{ minHeight: 48, minWidth: 48 }}>
                {t_('Add')}
              </Button>
              )
            : null}
        </Stack>
      </Stack>
    </Box>
  )
}

FabricationDocumentAddForm.propTypes = {
  groups: PropTypes.array,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func
}

// Se exporta para S126: la rejilla es responsive por `sx` y jsdom NO evalúa media queries, así
// que lo único honesto que se puede afirmar es el estilo DECLARADO. Un test que midiera el ancho
// real daría verde siempre, mirase lo que mirase.
export { TYPE_GRID_SX }
export default FabricationDocumentAddForm
